import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { TONE } from '../../data/constants'
import { getFullSubsectionGap, getPartialSubsectionGaps } from '../../data/scoring'
import GapIcon from '../../components/GapIcon/GapIcon'
import { useAIRiskReportExportMode } from '../../components/RenderMode/AIRiskReportRenderModeProvider'
import ScoreProgress from '../../components/ScoreProgress/ScoreProgress'
import styles from './SectionNavCards.module.scss'

function CardRow({ subsection }) {
  const hasValue = Number.isFinite(subsection.score.value)
  const tone = hasValue ? subsection.score.tone : TONE.NEUTRAL
  const gap = getFullSubsectionGap(subsection)
  const partialGap = getPartialSubsectionGaps(subsection)[0]

  return (
    <div className={`${styles.row} d-grid column-gap-2 row-gap-1 align-items-baseline`}>
      <span className={styles.label}>
        <FormattedMessage id={subsection.titleId} />
      </span>
      {hasValue && (
        <span className={`${styles.value} ${styles[tone] ?? ''} d-inline-flex align-items-center gap-1`}>
          {subsection.score.value}%
          {partialGap && <GapIcon gap={partialGap} id={`ai-risk-nav-partial-gap-${subsection.id}`} />}
        </span>
      )}
      {!hasValue && (
        <span className={styles.value}>
          <GapIcon gap={gap} id={`ai-risk-nav-gap-${subsection.id}`} />
        </span>
      )}
      <span className={styles.progressCell}>
        <ScoreProgress
          value={subsection.score.value}
          tone={tone}
          showValue={false}
          reserveValueSpace={false}
          size="compact"
          disabled={!hasValue}
        />
      </span>
    </div>
  )
}

CardRow.propTypes = {
  subsection: PropTypes.shape({
    id: PropTypes.string.isRequired,
    titleId: PropTypes.string.isRequired,
    status: PropTypes.string,
    score: PropTypes.shape({ value: PropTypes.number, tone: PropTypes.string }).isRequired,
    gaps: PropTypes.arrayOf(
      PropTypes.shape({
        scope: PropTypes.string,
        reason: PropTypes.string,
        requiredLicense: PropTypes.string,
        capabilityId: PropTypes.string,
      }),
    ).isRequired,
  }).isRequired,
}

function NavCard({ section }) {
  const isExportMode = useAIRiskReportExportMode()
  const content = (
    <>
      <div className={styles.title}>
        <FormattedMessage id={section.titleId} />
      </div>
      <div className="d-flex flex-column gap-2">
        {section.subsections.map((subsection) => (
          <CardRow key={subsection.id} subsection={subsection} />
        ))}
      </div>
      {!isExportMode && (
        <span className={`${styles.jump} mt-auto`}>
          <FormattedMessage id="AI_RISK_REPORT.VIEW_DETAILS" /> ↓
        </span>
      )}
    </>
  )

  if (isExportMode) {
    return <div className={`${styles.card} d-flex flex-column`}>{content}</div>
  }

  return (
    <a href={`#${section.anchor}`} className={`${styles.card} d-flex flex-column`}>
      {content}
    </a>
  )
}

NavCard.propTypes = {
  section: PropTypes.shape({
    titleId: PropTypes.string.isRequired,
    anchor: PropTypes.string.isRequired,
    score: PropTypes.shape({ value: PropTypes.number, tone: PropTypes.string }).isRequired,
    subsections: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  }).isRequired,
}

export default function SectionNavCards({ scoring = { sections: [] } }) {
  return (
    <div className={`${styles.grid} d-grid`}>
      {scoring.sections.map((section) => (
        <NavCard key={section.id} section={section} />
      ))}
    </div>
  )
}

SectionNavCards.propTypes = {
  scoring: PropTypes.shape({
    sections: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        score: PropTypes.shape({ value: PropTypes.number, tone: PropTypes.string }),
        subsections: PropTypes.arrayOf(PropTypes.shape({})),
      }),
    ),
  }),
}
